// Research session loading and management

import { getAuthHeaders, authenticatedFetch } from '../api/auth.js';
import {
    setCurrentResearchId,
    getCurrentResearchId,
    clearActiveResearchIdOnServer
} from '../state/research.js';
import { markSearchPerformed } from '../state/session.js';
import { moveSearchBoxToBottom, moveHeaderBoxToTop } from './positioning.js';
import { getUserCitationFormat, generateReferencesHTML, cacheCitations, createCitationFormatDropdown } from './citations.js';
import { replaceTimestampWithSearchId } from './html.js';
import { closeResearchHistory } from './sidebar.js';
import { createCompareButton } from './compare-button.js';
import { enableEditMode } from './edit-mode.js';
import { copyText, download, logResponse, goToPubMed } from './actions.js';

// Callback for handleSearch - will be set from pre/legacy.js
let handleSearchCallback = null;

export function setResearchSessionLoaderCallbacks(callbacks) {
    if (callbacks.handleSearch) {
        handleSearchCallback = callbacks.handleSearch;
    }
}

/**
 * Load a shared research from /share/<research_id>.
 * Fetches from /api/share/{id} (no auth required).
 * Hides search box, userQuery actions, suggested chips.
 * Shows toolbars only if the viewer is a Pro user.
 */
export async function loadSharedResearch(researchId) {
    try {
        const response = await fetch(`/api/share/${encodeURIComponent(researchId)}`, {
            headers: getAuthHeaders()
        });
        if (!response.ok) {
            if (response.status === 404) {
                alert('Research not found');
                window.location.href = '/';
                return;
            }
            throw new Error(`Failed to load shared research: ${response.status}`);
        }
        const data = await response.json();
        const searches = data.searches || [];
        const suggestionsHtml = data.suggestions_html || '';

        let searchFooterToAppend = null;
        if (searches.length > 0) {
            // Share view: always hide search box
            const searchBoxEarly = document.getElementById('searchBox');
            const searchFooter = document.getElementById('search-footer');
            if (searchBoxEarly) {
                if (searchFooter && searchFooter.parentNode === searchBoxEarly) {
                    searchBoxEarly.removeChild(searchFooter);
                    searchFooterToAppend = searchFooter;
                }
                searchBoxEarly.style.display = 'none';
            }
        }

        setCurrentResearchId(researchId);
        markSearchPerformed();

        const container = document.querySelector('.container');
        const headerBox = document.getElementById('headerBox');
        const searchBox = document.getElementById('searchBox');

        const childrenToRemove = [];
        for (const child of Array.from(container.children)) {
            if (child.id !== 'headerBox' && child.id !== 'searchBox') {
                childrenToRemove.push(child);
            }
        }
        childrenToRemove.forEach(child => child.remove());

        if (headerBox) moveHeaderBoxToTop();
        if (searchBox) moveSearchBoxToBottom();

        if (typeof window.bms?.hideExampleStrip === 'function') {
            window.bms.hideExampleStrip();
        } else if (typeof hideExampleStrip === 'function') {
            hideExampleStrip();
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });

        const searchInput = document.getElementById('searchInput');
        if (searchInput) {
            searchInput.placeholder = 'Follow-up search';
            searchInput.value = '';
            requestAnimationFrame(() => {
                if (typeof adjustTextareaHeight === 'function') {
                    adjustTextareaHeight();
                } else {
                    searchInput.style.setProperty('height', '56px', 'important');
                    searchInput.style.setProperty('min-height', '56px', 'important');
                    searchInput.style.setProperty('overflow', 'hidden', 'important');
                }
            });
        }

        if (searches.length > 0) {
            const lastSearchIndex = searches.length - 1;
            const citationFormat = await getUserCitationFormat();
            const showToolbars = true; // Always show toolbars on share page

            searches.forEach((search, index) => {
                const searchId = search.search_id;
                const userQuery = search.user_query || '';
                const responseHtml = search.response_html || '';
                const citations = search.citations || [];
                const docCount = search.doc_count || 0;

                let processedResponseHtml = replaceTimestampWithSearchId(responseHtml, searchId);
                const referencesHTML = generateReferencesHTML(citations, citationFormat);

                const searchResultWrapper = document.createElement('div');
                searchResultWrapper.id = `search-result-wrapper-${searchId}`;
                searchResultWrapper.className = 'search-result-wrapper';
                searchResultWrapper.setAttribute('data-search-id', searchId);

                const userQueryDiv = document.createElement('div');
                userQueryDiv.id = `userQuery-${searchId}`;
                userQueryDiv.className = 'userQuery-container';

                const actions = document.createElement('div');
                actions.className = 'userQuery-actions';
                actions.style.display = 'none';

                const userQueryP = document.createElement('p');
                userQueryP.className = 'userQuery';
                userQueryP.textContent = DOMPurify.sanitize(userQuery).replace(/\n/g, '');
                userQueryDiv.appendChild(userQueryP);
                userQueryDiv.appendChild(actions);
                searchResultWrapper.appendChild(userQueryDiv);

                const responseBodyWrapper = document.createElement('div');
                responseBodyWrapper.id = `response-body-wrapper-${searchId}`;
                responseBodyWrapper.className = 'response-body show';
                responseBodyWrapper.setAttribute('data-timestamp', searchId);
                responseBodyWrapper.setAttribute('data-search-id', searchId);

                const outputDiv = document.createElement('div');
                outputDiv.id = `output-${searchId}`;
                outputDiv.className = 'markdown-body';
                outputDiv.innerHTML = processedResponseHtml;
                responseBodyWrapper.appendChild(outputDiv);

                const referencesSection = document.createElement('div');
                referencesSection.id = `references-${searchId}`;
                referencesSection.className = referencesHTML ? 'references-section visible' : 'references-section hidden';
                if (referencesHTML) referencesSection.innerHTML = referencesHTML;
                if (citations.length > 0) cacheCitations(searchId, citations);
                responseBodyWrapper.appendChild(referencesSection);

                const footerNote = document.createElement('div');
                footerNote.id = `footer-note-${searchId}`;
                footerNote.className = 'hidden visible';
                const compactFormatter = new Intl.NumberFormat('en-US');
                const searchDateStr = (search.timestamp != null)
                    ? new Date(search.timestamp * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '';
                footerNote.innerHTML = `<p class="footer-note-line"><small><span class="footer-note-count">${compactFormatter.format(docCount)} results found in PubMed</span></small><small class="footer-note-date">${searchDateStr}</small></p>`;
                responseBodyWrapper.appendChild(footerNote);

                searchResultWrapper.appendChild(responseBodyWrapper);

                const toolbar = document.createElement('div');
                toolbar.id = `toolbar-${searchId}`;
                toolbar.className = (showToolbars && docCount > 0) ? 'visible' : 'hidden';

                const toolbarHTML = `
                    <button class="icon-btn" id="thumbsup-${searchId}" data-tooltip="Good Response" data-short-label="Good Response"><span class="material-symbols-rounded notranslate">thumb_up</span></button>
                    <button class="icon-btn" id="thumbsdown-${searchId}" data-tooltip="Bad Response" data-short-label="Bad Response"><span class="material-symbols-rounded notranslate">thumb_down</span></button>
                    <button class="icon-btn" id="copybtn-${searchId}" data-tooltip="Copy Response" data-short-label="Copy Response"><span class="material-symbols-rounded notranslate">content_copy</span></button>
                    <button class="icon-btn" id="downloadbtn-${searchId}" data-tooltip="Download PDF" data-short-label="Download PDF"><span class="material-symbols-rounded notranslate">download</span></button>
                    <button class="icon-btn" id="pubmedbtn-${searchId}" data-tooltip="Reproducible PubMed Results (1-click)" data-short-label="PubMed Results" data-search-id="${searchId}"><span class="material-symbols-rounded notranslate">quick_reference_all</span></button>
                    <button class="icon-btn" id="comparebtn-${searchId}" data-tooltip="Summary Table — compare studies side-by-side" data-short-label="Summary Table" data-search-id="${searchId}"><span class="material-symbols-rounded notranslate">table_view</span></button>
                `;
                toolbar.innerHTML = toolbarHTML;
                const pubmedBtn = toolbar.querySelector(`#pubmedbtn-${searchId}`);
                if (pubmedBtn) {
                    toolbar.insertBefore(createCitationFormatDropdown(searchId), pubmedBtn);
                }
                searchResultWrapper.appendChild(toolbar);

                if (index === lastSearchIndex && suggestionsHtml) {
                    let processedSuggestionsHtml = replaceTimestampWithSearchId(suggestionsHtml, searchId);
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = processedSuggestionsHtml;
                    const suggestionsElement = tempDiv.firstElementChild;
                    if (suggestionsElement) {
                        searchResultWrapper.appendChild(suggestionsElement);
                    }
                }

                container.appendChild(searchResultWrapper);
            });

            // Re-assert share research ID — auth check may have overwritten it since line 64
            setCurrentResearchId(researchId);
            reinitializeLoadedResearch();

            const suggestedChipsContainers = document.querySelectorAll('.suggested-chips-container');
            suggestedChipsContainers.forEach((containerEl) => {
                const title = containerEl.querySelector('.suggested-chips-title');
                const wrapper = containerEl.querySelector('.suggested-chips-wrapper');
                const newResearchBtn = containerEl.querySelector('.new-search-button-container');
                if (title) title.style.display = 'none';
                if (wrapper) wrapper.style.display = 'none';
                if (newResearchBtn) newResearchBtn.style.display = 'none';
            });

            console.log('[Research] Loaded shared research with', searches.length, 'searches');
        }

        if (searchFooterToAppend && container) {
            container.appendChild(searchFooterToAppend);
        }

        // Add "New Research" button at the end of the share page
        const newResearchContainer = document.createElement('div');
        newResearchContainer.className = 'new-search-button-container';
        newResearchContainer.style.marginTop = '24px';
        newResearchContainer.style.marginBottom = '24px';
        const newResearchBtn = document.createElement('button');
        newResearchBtn.type = 'button';
        newResearchBtn.className = 'new-search-button';
        newResearchBtn.innerHTML = '<span class="material-symbols-rounded notranslate">add</span> New Research';
        newResearchBtn.addEventListener('click', async () => {
            await clearActiveResearchIdOnServer();
            setCurrentResearchId(null);
            sessionStorage.removeItem('bms_session_id');
            sessionStorage.removeItem('bms_has_searched');
            window.location.href = '/';
        });
        newResearchContainer.appendChild(newResearchBtn);
        if (container) container.appendChild(newResearchContainer);

        // Remove share-loading state (was set in head to prevent flash)
        document.documentElement.classList.remove('share-loading');
        const shareLoadingEl = document.getElementById('bms-share-loading');
        if (shareLoadingEl) shareLoadingEl.style.display = 'none';
    } catch (error) {
        console.error('[Research] Error loading shared research:', error);
        console.error('[Research] Stack:', error?.stack);
        document.documentElement.classList.remove('share-loading');
        const shareLoadingEl = document.getElementById('bms-share-loading');
        if (shareLoadingEl) shareLoadingEl.style.display = 'none';
        alert('Error loading shared research: ' + (error?.message || String(error)));
        window.location.href = '/';
    }
}

export async function loadResearchSession(researchId) {
    try {
        const response = await authenticatedFetch(`/research-sessions/${researchId}`, {
            headers: getAuthHeaders()
        });
        
        if (response.ok) {
            const data = await response.json();
            const searches = data.searches || [];
            const suggestionsHtml = data.suggestions_html || '';
            
            let searchFooterToAppend = null;
            const isOnSharePage = window.location.pathname.startsWith('/share/');
            if (isOnSharePage) {
                // Viewing a shared session — hide search box for all users (not their session to continue)
                const searchBoxEarly = document.getElementById('searchBox');
                const searchFooter = document.getElementById('search-footer');
                if (searchBoxEarly) {
                    if (searchFooter && searchFooter.parentNode === searchBoxEarly) {
                        searchBoxEarly.removeChild(searchFooter);
                        searchFooterToAppend = searchFooter;
                    }
                    searchBoxEarly.style.display = 'none';
                }
            } else {
                // Loading own history — always show search box
                const searchBoxEarly = document.getElementById('searchBox');
                if (searchBoxEarly) searchBoxEarly.style.display = '';
            }

            // If loading own session while URL is still on a share page, correct the URL
            if (isOnSharePage && localStorage.getItem('access_token')) {
                window.history.replaceState({}, document.title, window.location.origin + '/');
            }
            
            // Set current research session
            setCurrentResearchId(researchId);
            markSearchPerformed();
            
            // Clear current page state (but don't navigate)
            const container = document.querySelector('.container');
            
            // Get references to headerBox and searchBox before clearing
            const headerBox = document.getElementById('headerBox');
            const searchBox = document.getElementById('searchBox');
            
            // Remove all user queries and responses from container
            // Keep only headerBox and searchBox (they are direct children)
            const childrenToRemove = [];
            for (const child of Array.from(container.children)) {
                // Keep headerBox and searchBox, remove everything else
                if (child.id !== 'headerBox' && child.id !== 'searchBox') {
                    childrenToRemove.push(child);
                }
            }
            childrenToRemove.forEach(child => child.remove());
            
            
            // Ensure header and search box are in correct positions
            if (headerBox) {
                moveHeaderBoxToTop();
            }
            if (searchBox) {
                moveSearchBoxToBottom();
            }
            
            // Hide example strip when research is loaded
            if (typeof window.bms?.hideExampleStrip === 'function') {
                window.bms.hideExampleStrip();
            } else if (typeof hideExampleStrip === 'function') {
                hideExampleStrip();
            }
            
            // Scroll to top of the page
            window.scrollTo({ top: 0, behavior: 'smooth' });
            
            // Update placeholder for follow-up queries
            const searchInput = document.getElementById('searchInput');
            if (searchInput) {
                searchInput.placeholder = "Follow-up search";
                // Clear the textarea and reset height to default (56px for bottom-positioned)
                searchInput.value = '';
                // Use requestAnimationFrame to ensure DOM has updated before adjusting height
                requestAnimationFrame(() => {
                    // Call adjustTextareaHeight if available (from post-script.js)
                    if (typeof adjustTextareaHeight === 'function') {
                        adjustTextareaHeight();
                    } else {
                        // Fallback: directly set the height for bottom-positioned searchbox
                        searchInput.style.setProperty("height", "56px", "important");
                        searchInput.style.setProperty("min-height", "56px", "important");
                        searchInput.style.setProperty("overflow", "hidden", "important");
                    }
                });
            }
            
            // Generate HTML for each search
            if (searches.length > 0) {
                const lastSearchIndex = searches.length - 1;
                // Get user's citation format preference once for all searches
                const citationFormat = await getUserCitationFormat();
                
                searches.forEach((search, index) => {
                    const searchId = search.search_id;
                    const userQuery = search.user_query || '';
                    const responseHtml = search.response_html || '';
                    const citations = search.citations || [];
                    const docCount = search.doc_count || 0;
                    
                    // Replace timestamp with search_id in response_html
                    let processedResponseHtml = replaceTimestampWithSearchId(responseHtml, searchId);
                    
                    // Generate references HTML from citations using user's preferred format
                    const referencesHTML = generateReferencesHTML(citations, citationFormat);
                    
                    // Build the search result wrapper HTML
                    const searchResultWrapper = document.createElement('div');
                    searchResultWrapper.id = `search-result-wrapper-${searchId}`;
                    searchResultWrapper.className = 'search-result-wrapper';
                    searchResultWrapper.setAttribute('data-search-id', searchId);
                    
                    // Create userQuery container
                    const userQueryDiv = document.createElement('div');
                    userQueryDiv.id = `userQuery-${searchId}`;
                    userQueryDiv.className = 'userQuery-container';
                    
                    // Create actions container (direct edit-query button)
                    const actions = document.createElement('div');
                    actions.className = 'userQuery-actions';

                    const actionsButton = document.createElement('button');
                    actionsButton.type = 'button';
                    actionsButton.className = 'userQuery-actions-btn';
                    actionsButton.setAttribute('data-tooltip', 'Edit Query');
                    actionsButton.innerHTML = '<span class="material-symbols-rounded notranslate">edit</span>';
                    actionsButton.addEventListener('click', (event) => {
                        event.stopPropagation();
                        enableEditMode(userQueryDiv, searchId);
                    });

                    actions.appendChild(actionsButton);

                    const userQueryP = document.createElement('p');
                    userQueryP.className = 'userQuery';
                    userQueryP.textContent = DOMPurify.sanitize(userQuery).replace(/\n/g, '');

                    // Append userQuery first, then actions (edit button on the right)
                    userQueryDiv.appendChild(userQueryP);
                    userQueryDiv.appendChild(actions);
                    searchResultWrapper.appendChild(userQueryDiv);
                    
                    // Create response body wrapper
                    const responseBodyWrapper = document.createElement('div');
                    responseBodyWrapper.id = `response-body-wrapper-${searchId}`;
                    responseBodyWrapper.className = 'response-body show';
                    responseBodyWrapper.setAttribute('data-timestamp', searchId);
                    responseBodyWrapper.setAttribute('data-search-id', searchId);
                    
                    // Create output div with response_html
                    const outputDiv = document.createElement('div');
                    outputDiv.id = `output-${searchId}`;
                    outputDiv.className = 'markdown-body';
                    outputDiv.innerHTML = processedResponseHtml;
                    responseBodyWrapper.appendChild(outputDiv);
                    
                    // Create references section
                    const referencesSection = document.createElement('div');
                    referencesSection.id = `references-${searchId}`;
                    referencesSection.className = referencesHTML ? 'references-section visible' : 'references-section hidden';
                    if (referencesHTML) {
                        referencesSection.innerHTML = referencesHTML;
                    }
                    if (citations.length > 0) cacheCitations(searchId, citations);
                    responseBodyWrapper.appendChild(referencesSection);
                    
                    // Create footer note
                    const footerNote = document.createElement('div');
                    footerNote.id = `footer-note-${searchId}`;
                    footerNote.className = 'hidden visible';
                    const compactFormatter = new Intl.NumberFormat('en-US');
                    const searchDateStr = (search.timestamp != null)
                        ? new Date(search.timestamp * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '';
                    footerNote.innerHTML = `<p class="footer-note-line"><small><span class="footer-note-count">${compactFormatter.format(docCount)} results found in PubMed</span></small><small class="footer-note-date">${searchDateStr}</small></p>`;
                    responseBodyWrapper.appendChild(footerNote);
                    
                    searchResultWrapper.appendChild(responseBodyWrapper);
                    
                    // Create toolbar
                    const toolbar = document.createElement('div');
                    toolbar.id = `toolbar-${searchId}`;
                    toolbar.className = docCount > 0 ? 'visible' : 'hidden';
                    
                    const toolbarHTML = `
                        <button class="icon-btn" id="thumbsup-${searchId}" data-tooltip="Good Response" data-short-label="Good Response"><span class="material-symbols-rounded notranslate">thumb_up</span></button>
                        <button class="icon-btn" id="thumbsdown-${searchId}" data-tooltip="Bad Response" data-short-label="Bad Response"><span class="material-symbols-rounded notranslate">thumb_down</span></button>
                        <button class="icon-btn" id="copybtn-${searchId}" data-tooltip="Copy Response" data-short-label="Copy Response"><span class="material-symbols-rounded notranslate">content_copy</span></button>
                        <button class="icon-btn" id="downloadbtn-${searchId}" data-tooltip="Download PDF" data-short-label="Download PDF"><span class="material-symbols-rounded notranslate">download</span></button>
                        <button class="icon-btn" id="pubmedbtn-${searchId}" data-tooltip="Reproducible PubMed Results (1-click)" data-short-label="PubMed Results" data-search-id="${searchId}"><span class="material-symbols-rounded notranslate">quick_reference_all</span></button>
                        <button class="icon-btn" id="comparebtn-${searchId}" data-tooltip="Summary Table — compare studies side-by-side" data-short-label="Summary Table" data-search-id="${searchId}"><span class="material-symbols-rounded notranslate">table_view</span></button>
                    `;
                    toolbar.innerHTML = toolbarHTML;
                    const pubmedBtnSession = toolbar.querySelector(`#pubmedbtn-${searchId}`);
                    if (pubmedBtnSession) {
                        toolbar.insertBefore(createCitationFormatDropdown(searchId), pubmedBtnSession);
                    }
                    searchResultWrapper.appendChild(toolbar);
                    
                    // Add suggestions_html for the last search
                    if (index === lastSearchIndex && suggestionsHtml) {
                        let processedSuggestionsHtml = replaceTimestampWithSearchId(suggestionsHtml, searchId);
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = processedSuggestionsHtml;
                        const suggestionsElement = tempDiv.firstElementChild;
                        if (suggestionsElement) {
                            searchResultWrapper.appendChild(suggestionsElement);
                        }
                    }
                    
                    container.appendChild(searchResultWrapper);
                });
                
                // Re-initialize event handlers for all elements in the loaded HTML
                reinitializeLoadedResearch();
                
                console.log('[Research] Loaded research session with', searches.length, 'searches');
            } else {
                console.log('[Research] No searches found for research session');
            }

            // Append search-footer to container for non-Pro users (was moved out of searchBox when it was hidden)
            if (searchFooterToAppend && container) {
                container.appendChild(searchFooterToAppend);
            }
            
            // Close research history sidebar
            closeResearchHistory();
        } else {
            console.error('[Research] Failed to load research session');
            alert('Failed to load research session');
        }
    } catch (error) {
        console.error('[Research] Error loading research session:', error);
        alert('Error loading research session');
    }
}

function reinitializeLoadedResearch() {
    const currentResearchId = getCurrentResearchId();
    
    // Re-initialize all compare buttons in the loaded HTML
    const compareButtons = document.querySelectorAll('[id^="comparebtn-"]');
    compareButtons.forEach(button => {
        const timestamp = button.id.replace('comparebtn-', '');
        // Try to get searchId from data attribute on button first
        let searchId = button.getAttribute('data-search-id');
        
        // If not found, try to get from search-result-wrapper or response-body-wrapper
        if (!searchId) {
            const searchResultWrapper = document.getElementById(`search-result-wrapper-${timestamp}`);
            if (searchResultWrapper) {
                searchId = searchResultWrapper.getAttribute('data-search-id');
            }
            if (!searchId) {
                const responseBodyWrapper = document.getElementById(`response-body-wrapper-${timestamp}`);
                if (responseBodyWrapper) {
                    searchId = responseBodyWrapper.getAttribute('data-search-id');
                }
            }
        }
        
        // If still not found, try to extract from onclick attribute (for backwards compatibility)
        if (!searchId) {
            // Try to find from searches array by matching timestamp
            // Note: We'll try to match by order if timestamp doesn't match exactly
        }
        
        // If we have searchId, reinitialize the compare button
        if (searchId) {
            // Remove existing onclick handlers and re-setup
            button.removeAttribute('onclick');
            createCompareButton(timestamp, searchId);
        }
    });
    
    // Re-initialize userQuery edit-query buttons
    const userQueryActions = document.querySelectorAll('.userQuery-actions');
    userQueryActions.forEach(actions => {
        const actionsButton = actions.querySelector('.userQuery-actions-btn');
        if (!actionsButton) return; // Skip if no button (e.g. share view)
        const userQueryDiv = actions.closest('.userQuery-container');
        if (!userQueryDiv) return;
        const timestamp = userQueryDiv.id.replace('userQuery-', '');

        // Re-setup actions button click handler
        const newActionsButton = actionsButton.cloneNode(true);
        actionsButton.parentNode.replaceChild(newActionsButton, actionsButton);

        newActionsButton.addEventListener('click', (event) => {
            event.stopPropagation();
            enableEditMode(userQueryDiv, timestamp);
        });
    });
    
    // Re-initialize copy/download buttons (remove onclick, add event listeners)
    const copyButtons = document.querySelectorAll('[id^="copybtn-"]');
    copyButtons.forEach(button => {
        button.removeAttribute('onclick');
        button.addEventListener('click', function() {
            copyText(this);
        });
    });
    
    const downloadButtons = document.querySelectorAll('[id^="downloadbtn-"]');
    downloadButtons.forEach(button => {
        button.removeAttribute('onclick');
        button.addEventListener('click', function() {
            download(this);
        });
    });
    
    // Re-initialize thumbs up/down buttons
    const thumbsUpButtons = document.querySelectorAll('[id^="thumbsup-"]');
    thumbsUpButtons.forEach(button => {
        button.removeAttribute('onclick');
        const timestamp = button.id.replace('thumbsup-', '');
        button.addEventListener('click', function() {
            logResponse('Positive', timestamp);
        });
    });
    
    const thumbsDownButtons = document.querySelectorAll('[id^="thumbsdown-"]');
    thumbsDownButtons.forEach(button => {
        button.removeAttribute('onclick');
        const timestamp = button.id.replace('thumbsdown-', '');
        button.addEventListener('click', function() {
            logResponse('Negative', timestamp);
        });
    });
    
    // Re-initialize PubMed buttons
    const pubmedButtons = document.querySelectorAll('[id^="pubmedbtn-"]');
    pubmedButtons.forEach(button => {
        const onclickAttr = button.getAttribute('onclick');
        let searchId = null;
        
        if (onclickAttr && onclickAttr.includes('goToPubMed')) {
            // Extract searchId from onclick attribute
            const match = onclickAttr.match(/goToPubMed\("([^"]+)"/);
            if (match && match[1]) {
                searchId = decodeURIComponent(match[1]);
            }
        }
        
        // Also try data attribute
        if (!searchId) {
            searchId = button.getAttribute('data-search-id');
        }
        
        if (searchId) {
            button.removeAttribute('onclick');
            button.addEventListener('click', function() {
                goToPubMed(searchId, currentResearchId);
            });
        }
    });

    // Re-initialize suggested chips
    const suggestedChips = document.querySelectorAll('.suggested-chip');
    suggestedChips.forEach(chip => {
        const chipParent = chip.parentNode;
        const newChip = chip.cloneNode(true);
        const suggestionValue = chip.getAttribute('data-value') || chip.textContent;
        chipParent.replaceChild(newChip, chip);
        newChip.addEventListener('click', async () => {
            const allowed = window.bms?.checkSearchLimit ? await window.bms.checkSearchLimit() : true;
            if (!allowed) return;
            document.getElementById('searchInput').value = suggestionValue;
            if (handleSearchCallback) {
                handleSearchCallback(true);
            } else if (window.bms && window.bms.handleSearch) {
                window.bms.handleSearch(true);
            }
        });
    });

    // Re-initialize "New Research" buttons
    const newResearchButtons = document.querySelectorAll('.new-search-button');
    newResearchButtons.forEach(button => {
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);
        newButton.addEventListener('click', async () => {
            await clearActiveResearchIdOnServer();
            setCurrentResearchId(null);
            sessionStorage.removeItem('bms_session_id');
            sessionStorage.removeItem('bms_has_searched');
            window.location.href = '/';
        });
    });
}

// Helper function to wait for "New Search" button to be displayed, then save HTML
export function waitForNewSearchButtonAndSave(timestamp, retryCount = 0) {
    const currentResearchId = getCurrentResearchId();
    console.log(`[Research] waitForNewSearchButtonAndSave called - timestamp=${timestamp}, retryCount=${retryCount}, currentResearchId=${currentResearchId}`);
    if (!currentResearchId) {
        console.warn('[Research] No currentResearchId, skipping HTML save. This might happen if research_id was not received from backend headers.');
        return;
    }
    
    // The new search button is inside the suggested-chips-container for this timestamp
    const suggestedChipsContainer = document.getElementById(`suggested-chips-${timestamp}`);
    if (!suggestedChipsContainer) {
        // If suggestions container doesn't exist, wait a bit and try again
        if (retryCount < 10) { // Max 10 retries (2 seconds total)
            setTimeout(() => waitForNewSearchButtonAndSave(timestamp, retryCount + 1), 200);
            return;
        }
        // If we've exhausted retries, save anyway
        console.warn('[Research] Suggested chips container not found after retries, saving HTML anyway');
        setTimeout(() => {
            const currentResearchId = getCurrentResearchId();
            if (currentResearchId) {
                saveSuggestionsHTML().catch(error => {
                    console.error('[Research] Error in saveSuggestionsHTML:', error);
                });
            }
        }, 300);
        return;
    }
    
    // Check if the new search button exists within the suggested chips container
    const newSearchButton = suggestedChipsContainer.querySelector('.new-search-button');
    if (!newSearchButton) {
        // Button not found yet, wait and try again (with max retries)
        if (retryCount < 10) { // Max 10 retries (2 seconds total)
            setTimeout(() => waitForNewSearchButtonAndSave(timestamp, retryCount + 1), 200);
            return;
        }
        // If we've exhausted retries, save anyway
        console.warn('[Research] New search button not found after retries, saving HTML anyway');
    } else {
        console.log('[Research] New search button found, proceeding to save HTML');
    }
    
    // Wait a small additional delay to ensure DOM is fully updated
    setTimeout(() => {
        const currentResearchId = getCurrentResearchId();
        if (currentResearchId) {
            console.log('[Research] Saving suggestions HTML after new search button is displayed');
            saveSuggestionsHTML().catch(error => {
                console.error('[Research] Error in saveSuggestionsHTML:', error);
            });
        }
    }, 300); // Small delay to ensure button is fully rendered
}

// Function to save the suggestions HTML
export async function saveSuggestionsHTML() {
    const currentResearchId = getCurrentResearchId();
    if (!currentResearchId) {
        console.log('[Research] No current research session, skipping suggestions save');
        return;
    }
    
    try {
        console.log('[Research] Starting saveSuggestionsHTML for research_id:', currentResearchId);
        
        // Get all search-result-wrapper elements to find the last one
        const container = document.querySelector('.container');
        if (!container) {
            console.error('[Research] Container not found');
            return;
        }
        
        // Get all search-result-wrapper elements
        const searchResultWrappers = container.querySelectorAll('.search-result-wrapper');
        
        if (searchResultWrappers.length === 0) {
            console.log('[Research] No search-result-wrapper elements found');
            return;
        }
        
        // Find the last (most recent) search to get suggestions_html
        let lastSearchSuggestions = null;
        let lastTimestamp = -1;
        
        searchResultWrappers.forEach((wrapper) => {
            // Extract timestamp or search_id from wrapper ID (search-result-wrapper-${id})
            const wrapperId = wrapper.id;
            const idMatch = wrapperId.match(/search-result-wrapper-(.+)/);
            if (idMatch) {
                const id = idMatch[1];
                // Try to parse as number for timestamp, otherwise use as search_id
                const timestamp = /^\d+$/.test(id) ? parseInt(id, 10) : id;
                
                // Find the most recent search (highest timestamp, or last one if using search_id)
                if (typeof timestamp === 'number' && timestamp > lastTimestamp) {
                    lastTimestamp = timestamp;
                    // Find the suggested-chips element for this search
                    const suggestionsElement = document.getElementById(`suggested-chips-${id}`);
                    if (suggestionsElement) {
                        lastSearchSuggestions = suggestionsElement.outerHTML;
                    }
                } else if (typeof timestamp === 'string') {
                    // If using search_id, get the last wrapper's suggestions
                    const suggestionsElement = document.getElementById(`suggested-chips-${id}`);
                    if (suggestionsElement && !lastSearchSuggestions) {
                        lastSearchSuggestions = suggestionsElement.outerHTML;
                    }
                }
            }
        });
        
        // Get the last wrapper's suggestions (fallback: just get the last one)
        if (!lastSearchSuggestions && searchResultWrappers.length > 0) {
            const lastWrapper = searchResultWrappers[searchResultWrappers.length - 1];
            const lastWrapperId = lastWrapper.id.replace('search-result-wrapper-', '');
            const suggestionsElement = document.getElementById(`suggested-chips-${lastWrapperId}`);
            if (suggestionsElement) {
                lastSearchSuggestions = suggestionsElement.outerHTML;
            }
        }
        
        // Save suggestions_html to backend
        const authHeaders = getAuthHeaders();
        
        const requestBody = {
            research_id: currentResearchId,
            suggestions_html: lastSearchSuggestions || ''
        };
        
        const response = await authenticatedFetch('/save_research', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...authHeaders
            },
            body: JSON.stringify(requestBody)
        });
        
        if (response.ok) {
            const result = await response.json();
            console.log('[Research] Saved suggestions_html successfully:', result);
        } else {
            const errorText = await response.text();
            console.error('[Research] Failed to save suggestions_html. Status:', response.status, 'Error:', errorText);
        }
    } catch (error) {
        console.error('[Research] Error saving suggestions_html:', error);
        console.error('[Research] Error stack:', error.stack);
    }
}
